/** /pre-trip-item-categories/:id/:tab? — pre-trip item category editor shell.
 *  Tabs: General + Used by (the category's child items). Delete CASCADE-removes
 *  the category's items, so the confirm message states the item count. System-
 *  default categories are read-only: no delete button. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as typesApi from '@/api/preTripItemTypes';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { preTripItemsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';

import { renderingTypeActions, type CatalogCtx } from '../catalog/catalogLinks';

import { PreTripItemCategoryGeneralTab } from './tabs/PreTripItemCategoryGeneralTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function PreTripItemCategoryDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: type,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['pre_trip_item_types', 'detail', id],
    queryFn: () => typesApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => void refetch();

  const deleteMutation = useMutation({
    mutationFn: () => typesApi.remove(id),
    onSuccess: () => {
      snackbar.show('Category deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['pre_trip_item_types'] });
      qc.invalidateQueries({ queryKey: ['pre_trip_items'] });
      navigate('/pre-trip-items');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid category id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !type) return <Alert severity="error">Couldn’t load category.</Alert>;

  const ctx: CatalogCtx = { tenantId: type.tenant?.id };
  const renderingLabel = type.rendering_type_display || `Type ${type.rendering_type}`;

  const deleteMessage =
    type.item_count > 0
      ? `"${type.name}" and its ${type.item_count} item${type.item_count === 1 ? '' : 's'} will be deleted (cascade). This cannot be undone.`
      : `"${type.name}" will be deleted. This cannot be undone.`;

  return (
    <Box>
      <PageHeader
        helpStory="pre-trip-items"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={type.name}
        subtitle={
          <IdentitySubtitle
            tenant={type.tenant}
            tenantFallback="System default"
            grid={type.tenant ? { href: preTripItemsHref({ tenant: type.tenant.id }), label: 'Trip items of this operator' } : null}
            id={type.id}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <ValueActions chip actions={renderingTypeActions(type.rendering_type, renderingLabel, ctx)}>
              <Chip size="small" variant="outlined" label={renderingLabel} />
            </ValueActions>
            {!type.is_system && (
              <PermissionGate perm="console_pretrip_manage">
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </PermissionGate>
            )}
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => navigate(`/pre-trip-item-categories/${id}/${v}`)}
        >
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <PreTripItemCategoryGeneralTab type={type} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedByPanel resource="pre_trip_item_types" id={type.id} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.pre_trip_item_type" objectPk={String(type.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to={preTripItemsHref({ tenant: type.tenant?.id })} style={{ color: '#0a1a3f' }}>
          ← Back to trip items
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this category?"
        message={deleteMessage}
        confirmLabel="Delete category"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
